import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Avatar } from 'antd';

import SupportChat from 'components/Container/PrivateLayout/SupportChat';
import PageLoader from 'components/Shared/PageLoader';
import { isEventFormFullscreen } from 'helpers/eventFormFullscreen';
import { useAuthTokens } from 'hooks/useAuthTokens';
import { useAuthUser } from 'hooks/useAuthUser';
import { LogOut } from 'lucide-react';
import loginService from 'services/login';
import userService from 'services/user/index';
import { BLINKET_APP_URL, WEBHOOK_URL } from 'settings';

import Layout from '@eduzz/ui-layout';

import { useAcpoAccountsQuery, useLoginAsEmailMutation } from './hooks';

const { Topbar, Content, Sidebar } = Layout;
const { Item, Group } = Sidebar;
const { UserMenu, UserMenuItem, ModeSwitcher } = Topbar;

const logoBlinketWhite = 'https://cdn.eduzzcdn.com/blinket/blinket-logo-white-v2.svg';
const logoBlinketDark = 'https://cdn.eduzzcdn.com/blinket/blinket-logo-dark-v2.svg';
const blinketIconMobile = 'https://cdn.eduzzcdn.com/blinket/eduzz-icon-v2.png';

interface PrivateLayoutProps {
  children: React.ReactNode;
}

const PrivateLayout = ({ children }: PrivateLayoutProps) => {
  const location = useLocation();
  const { data: userData } = useAuthUser();
  const { data: storedToken } = useAuthTokens();
  const loginAsEmailMutation = useLoginAsEmailMutation();
  const acpoAccountsQuery = useAcpoAccountsQuery(!!userData?.acpo_users?.length);

  const acpoAccountsByEmail = new Map((acpoAccountsQuery.data ?? []).map(account => [account.email, account]));

  const isProducer = !!userData?.is_producer;
  const isAccessControl = Boolean(userData?.acpo);

  useEffect(() => {
    if (!storedToken?.token) {
      loginService.auth();
    }
  }, [storedToken?.token]);

  useEffect(() => {
    if (userData?.id) {
      window.gtag('set', 'user_id', userData?.id);
    }
  }, [userData?.id]);

  const handleLogout = () => {
    if (localStorage.getItem('isSupport')) {
      localStorage.removeItem('isSupport');
    }

    userService.logout();
  };

  const loginByEmail = (email: string) => {
    loginAsEmailMutation.mutate(email);
  };

  if (isEventFormFullscreen(location.pathname)) {
    return <>{children}</>;
  }

  return (
    <Layout persistMode>
      {loginAsEmailMutation.isPending && <PageLoader />}

      <Topbar
        currentApplication='blinket'
        user={{
          name: userData?.user_name,
          belt: userData?.belt,
          avatar: userData?.photo
        }}
        logoByApplication='events'
        logo={logoBlinketWhite}
        logoDarkMode={logoBlinketDark}
        logoMobile={blinketIconMobile}
        logoMobileDarkMode={blinketIconMobile}
        id='eduzz-topbar-menu'
      >
        <ModeSwitcher />

        <UserMenu id='eduzz-topbar-menu-user'>
          <>
            {!!userData?.acpo_users &&
              userData.acpo_users
                .filter(otherUser => otherUser.email != userData.user_email)
                .map(otherUser => {
                  const account = acpoAccountsByEmail.get(otherUser.email);
                  const initialLetter = (!!account?.name ? account?.name : otherUser.email).charAt(0).toUpperCase();

                  return (
                    <UserMenuItem
                      id={`eduzz-topbar-menu-acpo-${encodeURIComponent(otherUser.email)}`}
                      key={otherUser.email}
                      onClick={() => loginByEmail(otherUser.email)}
                      icon={
                        <Avatar size='small' src={!!account?.photo ? account?.photo : undefined}>
                          {initialLetter}
                        </Avatar>
                      }
                    >
                      <span className='block truncate'>{!!account?.name ? account?.name : otherUser.email}</span>
                    </UserMenuItem>
                  );
                })}
          </>

          <UserMenuItem id='eduzz-topbar-menu-logout' onClick={handleLogout} icon={<LogOut size={24} />}>
            Sair
          </UserMenuItem>
        </UserMenu>

        <SupportChat />
      </Topbar>

      <Sidebar currentLocation={location.pathname}>
        <Group id='eduzz-sidebar-group-home' label='Início' className='mb-3'>
          <Item id='eduzz-sidebar-resume' as={NavLink} to='/home'>
            Resumo
          </Item>
          {isProducer && (
            <Item id='eduzz-sidebar-my-events' as={NavLink} to='/my-events'>
              Meus Eventos
            </Item>
          )}
          {!isAccessControl && (
            <Item id='eduzz-sidebar-my-tickets' as={NavLink} to='/my-tickets'>
              Meus Ingressos
            </Item>
          )}
          {isProducer && (
            <Item id='eduzz-sidebar-webhook' href={`${WEBHOOK_URL}/webhook/configs`} target='_blank' rel='noreferrer'>
              Webhook
            </Item>
          )}
          <Item
            id='eduzz-sidebar-marketplace'
            as={NavLink}
            to={{
              pathname: `${BLINKET_APP_URL}/marketplace`
            }}
            target='_blank'
            isExternal={true}
          >
            Marketplace
          </Item>
          {!isProducer && (
            <Item id='eduzz-sidebar-create-event' as={NavLink} to='/event'>
              Criar um evento
            </Item>
          )}
        </Group>
      </Sidebar>
      <Content>{children}</Content>
    </Layout>
  );
};

export default PrivateLayout;
